import { flushCartSync, getGuestCartConsumed, syncCart } from "@/api/CartApi";
import { CartToken } from "@/libs/cart-token/CartToken";
import {
  clearGeCheckoutMarker,
  GE_CHECKOUT_IN_FLIGHT_MS,
  readActiveGeCheckout,
  takeFirstReturnAfterGeCheckout,
} from "@/libs/global-e/checkout-marker";
import { getCartState } from "@/store/cart-store";

// GE 주문으로 **소비된** cart token 을 서버 판정으로 회수해 카트를 비운다. docs/global-e-storefront.md §4.7.3.
//
// 프론트의 1차 비우기는 GE `OnCheckoutStepLoaded`(CONFIRMATION) 콜백이다(`checkout-analytics.ts`). 그런데
// 결제가 체크아웃 페이지를 떠난 채 끝나면 그 콜백이 오지 않는다 → 카트가 남고 **같은 token 이 다음 주문에
// 재사용**된다(2026-09-29 dev 실측, `GE185010720JP`·`GE185023039JP` — GE 복귀 주소 설정 문제로 PayPal 이 사이트
// 루트로 돌아왔다. 10-01 GE 수정 뒤엔 체크아웃 페이지로 와서 콜백이 온다 → 이 회수는 안전망이다). 그래서 GE 가 SendOrder 에 싣는 `ClearCart:true` 를 mgmt 가 draft 존재로
// 판정해 주고(`GET /api/v1/guest-cart/consumed`), 브라우저가 그걸 물어 비운다.
//
// ★결제가 끝나지 않은 카트는 비우지 않는다 — GE draft 는 SendOrder(= 결제 뒤)에만 생긴다. 판정 불가(null)도
// 비우지 않는다. 복귀 URL 파라미터(`ts=Authorized`)로 비우지 않는 이유: 실측 `Authorized` 착지 중 2건은 주문이 없었다.
//
// 묻는 곳은 셋이다 — 전부 **표식(`checkout-marker.ts`)이 있을 때만** 묻는다(GE 결제를 시작한 적 없는 브라우저는 0회):
//   1. 페이지 로드: 복귀 착지(체크아웃 뒤 첫 비-GE 페이지 · 30분 안 외부 사이트발)면 폴링, 그 외엔 1회 (`GeConsumedCartWatcher`)
//   2. `/cart` 페이지로 **SPA 이동**할 때 1회 — 전체 로드면 1이 이미 묻는다                            (`GeConsumedCartWatcher`)
//   3. GE 체크아웃 진입 직전 1회 — 소비된 token 으로 새 GE 결제를 시작하지 않게                         (카트 버튼 · 바로구매)

/** 페이지 로드·카트 이동 때 1회 조회 상한. */
export const CONSUMED_LOOKUP_TIMEOUT_MS = 5000;
/** 결제 진입 직전 조회 상한 — 버튼이 이 조회에 묶이므로 짧게. 넘기면 묻지 않은 것으로 진행(종전 동작). */
export const CHECKOUT_GUARD_TIMEOUT_MS = 1500;
/**
 * 복귀 직후 폴링. SendOrder 는 복귀 **뒤에** 온다 — 실측 46~55초(5건, PayPal). 샘플이 적어 넉넉히 잡되 상한을 둔다.
 * 「첫 복귀」 폴링은 한 표식당 한 번이지만(`takeFirstReturnAfterGeCheckout`), **외부 사이트발 착지**는 30분 창 안이면 그때마다
 * 폴링한다(착지 새로고침 포함) — 한 번에 최대 3분이다. 놓치면 다음 페이지 로드·카트 이동이 회수한다.
 */
export const RETURN_POLL_INTERVAL_MS = 5000;
export const RETURN_POLL_MAX_MS = 3 * 60 * 1000;

/**
 * 소비된 token 을 정리한다. 멱등. 결제 종류(표식의 `direct`)에 따라 다르다:
 *
 * - **카트 결제**(기본): 카트 전체가 주문됐다 → 카트를 비우고 token 을 교체한다.
 * - **바로구매**(`direct`): 카트를 거치지 않았으므로 **카트는 사지 않은 것**이다 → token 만 교체하고, 남은 카트는
 *   새 token 으로 다시 싣는다. 비우면 사지 않은 카트가 사라진다(로그인이면 uid 카트까지 — syncCart([]) 경유).
 *
 * 순서가 중요하다 — `cart.clear()` 는 카트가 이미 비어 있어도 새 `[]` 를 만들어 **500ms debounced `syncCart([])`** 를
 * 건다(cart-store subscribe). 그걸 두고 호출부가 새 token 을 만들면(바로구매의 `writeGuestCartForCheckout`) 뒤늦게 발사된
 * 빈 sync 가 **새 token 의 카트를 비운다** → GE 가 빈 카트를 읽는다. 그래서 token 을 지운 **직후** `flushCartSync()` 로
 * 그 빈 sync 를 지금 밀어낸다 — token 이 없으니 guest-cart 에는 아무것도 안 가고, 로그인이면 uid 카트만 비워진다(맞는 결과).
 *
 * `CartToken.clear()` 를 직접 부르는 이유: 카트가 이미 비어 있으면(바로 결제) `clear()` 가 빔→빔이라 cart-store
 * subscribe 의 token 교체가 일어나지 않는다. 그러면 같은 token 이 다음 바로 결제에 재사용된다.
 */
export async function clearConsumedCart(): Promise<void> {
  const direct = readActiveGeCheckout()?.direct === true;
  CartToken.clear();
  clearGeCheckoutMarker();
  if (direct) {
    const { items, gameItems } = getCartState();
    if (items.length > 0 || gameItems.length > 0) {
      // 남은 카트를 새 token 에 싣는다 — 게스트 sync 는 token 을 만들지 않으므로(`doSync`) 여기서 발급한다.
      CartToken.ensure();
      syncCart(
        items.map((i) => ({ sku: i.sku, quantity: i.quantity })),
        gameItems.map((g) => ({
          gameResellId: g.gameResellId,
          platform: g.platform,
        })),
      );
    }
  } else {
    getCartState().clear();
  }
  await flushCartSync();
}

/**
 * 회수 결과. `"cleared"` = 카트를 비웠다(카트 결제로 소비) · `"rotated"` = token 만 교체하고 카트는 남겼다(바로구매로
 * 소비) · `null` = 아무것도 안 했다(표식 없음 · 아직 소비 전 · 판정 불가 · 예외).
 */
export type ConsumedCartOutcome = "cleared" | "rotated" | null;

/**
 * 표식이 있으면 1회 묻고, 소비됐으면 정리한다(`clearConsumedCart`).
 * **reject 하지 않는다** — 결제 버튼이 이 결과를 await 한다. 정리 중 예외는 「소비 안 됨」으로 접는다(조회 실패와 같은
 * fail-open). 종전 규약: GE 이동 전에 await 하는 것은 절대 reject 하지 않는다(#5015 `enqueueSync`).
 */
export async function clearCartIfConsumed(
  timeoutMs: number = CONSUMED_LOOKUP_TIMEOUT_MS,
): Promise<ConsumedCartOutcome> {
  try {
    const marker = readActiveGeCheckout();
    if (!marker) return null;
    const consumed = await getGuestCartConsumed(marker.token, timeoutMs);
    // 묻는 사이 token 이 바뀌었으면(다른 탭·경로가 이미 비웠다) 새 카트를 건드리지 않는다.
    if (consumed !== true || CartToken.get() !== marker.token) return null;
    const outcome: ConsumedCartOutcome = marker.direct ? "rotated" : "cleared";
    console.log(
      `[GlobalE] cart token already used by a GE order → ${
        outcome === "rotated"
          ? "rotating token (direct buy — cart kept)"
          : "clearing cart"
      }`,
    );
    await clearConsumedCart();
    return outcome;
  } catch (e) {
    console.warn("[GlobalE] consumed-cart recovery failed", e);
    return null;
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

/**
 * 이 페이지 로드가 **다른 사이트에서 넘어온** 착지인가. GE 결제 복귀는 GE 도메인이 referrer 다(dev 실측
 * `https://www.bglobale.com/`). 도메인을 하드코딩하지 않는다 — prod GE 도메인은 미확인이고, 「우리 origin 이 아니다」면 충분하다.
 * referrer 가 비면(정책으로 제거) false — 그땐 「첫 복귀」 판정만 남는다.
 */
function cameFromExternalSite(): boolean {
  try {
    const ref = document.referrer;
    return !!ref && new URL(ref).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * 페이지 로드 시 회수. GE 체크아웃 페이지 자체에서는 부르지 않는다(호출부가 거른다).
 *
 * 폴링(SendOrder 가 올 때까지)하는 착지 — 둘 중 하나:
 *   - 체크아웃 뒤 **첫** 비-GE 페이지 (`takeFirstReturnAfterGeCheckout` — 한 표식당 한 번)
 *   - 결제 진행 창 안에서 **외부 사이트에서 넘어온** 페이지 — 결제 중 다른 탭이 「첫 페이지」를 가져가도 실제 복귀
 *     탭이 폴링하게 한다(그 탭이 1회만 물으면 ~50초 뒤 오는 SendOrder 를 놓친다). 이쪽은 **소진하지 않는다** — 창 안의
 *     외부 착지마다(착지 새로고침 포함) 다시 폴링한다
 * 그 외엔 1회만 묻는다.
 */
export async function watchConsumedCartOnPageLoad(
  signal: AbortSignal,
  opts: { intervalMs?: number; maxMs?: number } = {},
): Promise<void> {
  const intervalMs = opts.intervalMs ?? RETURN_POLL_INTERVAL_MS;
  const maxMs = opts.maxMs ?? RETURN_POLL_MAX_MS;
  const marker = readActiveGeCheckout();
  if (!marker) return;
  // 첫 복귀 표시는 조건과 무관하게 먼저 소진한다(외부 착지가 먼저 와도 뒤 페이지가 다시 폴링하지 않게).
  const firstReturn = takeFirstReturnAfterGeCheckout();
  const externalLanding =
    cameFromExternalSite() &&
    Date.now() - marker.startedAt <= GE_CHECKOUT_IN_FLIGHT_MS;
  if (!firstReturn && !externalLanding) {
    await clearCartIfConsumed();
    return;
  }
  const deadline = Date.now() + maxMs;
  while (!signal.aborted) {
    if (await clearCartIfConsumed()) return;
    // 표식이 사라졌다 = 다른 경로(CONFIRMATION·다른 탭)가 이미 비웠거나 token 이 바뀌었다.
    if (!readActiveGeCheckout()) return;
    if (Date.now() + intervalMs > deadline) return;
    await sleep(intervalMs, signal);
  }
}
